import { defineStore } from 'pinia';
import { signalrListen } from '@lib/signalr';
import type { NotificationDto } from '@/ApiClient';
import { NotificationType, NotificationStatus } from '@/ApiClient';

interface NotificationWithProgress extends NotificationDto {
  progress?: number;
}

interface NotificationsState {
  loading: boolean;
  loaded: NotificationWithProgress[] | null;
  showNotificationTime: number;
  newNotifications: NotificationWithProgress[];
}

export const useNotifications = defineStore('notifications', {
  state: (): NotificationsState => ({
    loading: false,
    loaded: null,

    showNotificationTime: 1000 * 8,

    newNotifications: [],
  }),

  getters: {
    isLoading: (state): boolean => {
      return state.loading && state.loaded === null;
    },

    numberOfUnreadNotifications: (state): number => {
      if (!state.loaded) return 0;

      // Don't count notifications that are read or notifications that are loading.
      return state.loaded.filter((n) => n.status === NotificationStatus.Unread && n.type !== NotificationType.Progress)
        .length;
    },

    hasInProgressNotifications: (state): boolean => {
      if (!state.loaded) return false;

      return state.loaded.filter((n) => n.type === NotificationType.Progress).length > 0;
    },

    unreadNotifications: (state): NotificationWithProgress[] => {
      return state.loaded?.filter((item) => item.status !== NotificationStatus.Read) || [];
    },

    readNotifications: (state): NotificationWithProgress[] => {
      return state.loaded?.filter((item) => item.status === NotificationStatus.Read) || [];
    },
  },

  actions: {
    async loadAll() {
      this.loading = true;
      const notifications = await this.$api.notification_GetNotifications();

      this.loaded = notifications;
      this.loading = false;
    },

    listen() {
      signalrListen('NewNotification', this.newNotification, { listenForever: true });
      signalrListen('NotificationProgress', this.updateNotificationProgress, { listenForever: true });
    },

    newNotification(notification: NotificationWithProgress) {
      // The notification was just created, and the server and local computer might disagree with some small amount.
      // We set the "createdAt" time to local now, to ensure it's not immediately displayed as one minute old.
      notification.createdAt = new Date().toISOString().slice(0, -1);

      // Check if new notification is replacing an old one - in such case remove the old one immediately
      this.loaded = this.loaded!.filter((n) => n.id !== notification.replacingId);
      this.loaded!.unshift(notification);

      // Also replace any currently showing notifications
      this.newNotifications = this.newNotifications.filter((n) => n.id !== notification.replacingId);
      this.newNotifications.unshift(notification);

      setTimeout(() => {
        this.newNotifications = [...this.newNotifications.filter((n) => n.id !== notification.id)];
      }, this.showNotificationTime);
    },

    acknowledge(notificationId: number) {
      this.newNotifications = [...this.newNotifications.filter((n) => n.id !== notificationId)];
      this.markAsViewed(notificationId);
    },

    setAllAsViewed() {
      if (this.loaded) {
        const notificationsClone = [...this.loaded];

        notificationsClone
          .filter((n) => n.status === NotificationStatus.Unread)
          .forEach((n, i) => {
            this.markAsViewed(n.id);
            notificationsClone[i].status = NotificationStatus.Read;
          });

        this.loaded = notificationsClone;
      }
    },

    async dismiss(notificationId: number) {
      await this.$api.notification_Inactivate(notificationId);

      const indexToRemove = this.loaded!.findIndex((n) => n.id === notificationId);
      this.loaded!.splice(indexToRemove, 1);
    },

    async markAsViewed(notificationId: number) {
      await this.$api.notification_MarkAsViewed(notificationId);

      const index = this.loaded!.findIndex((n) => n.id === notificationId);
      if (index === -1) return;
      this.loaded![index].status = NotificationStatus.Read;
    },

    async dismissAll() {
      await this.$api.notification_InactivateAll();

      this.loaded = [];
    },

    updateNotificationProgress(notificationId: number, progressPercentage: number) {
      if (!this.loaded) return;

      const notification = this.loaded.find((n) => n.id === notificationId);
      if (notification) {
        notification.progress = progressPercentage;
      }

      const newNotification = this.newNotifications.find((n) => n.id === notificationId);
      if (newNotification) {
        newNotification.progress = progressPercentage;
      }
    },
  },
});
